import { SelectOption } from '@consigli/facade';
import { useGetProcessorJobsQuery, useStartPipelineMutation } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import {
  canSelectTextProcessor,
  DocumentPackageType,
  getPipelinesForPackage,
  JobType,
  PipelineName,
  PROCESSORS_BY_PACKAGE_TYPE,
} from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { useCallback, useMemo, useState } from 'react';

import { Select } from '@/components';
import { errorToast, successToast } from '@/components/toast';

interface StartPipelineMenuProps {
  projectId: number;
  packageId: string;
  jobType: JobType;
  documentPackageType?: DocumentPackageType;
}

const ALL_PROCESSORS_VALUE = 'ALL' as const;

export const StartPipelineMenu = ({
  projectId,
  packageId,
  jobType,
  documentPackageType,
}: StartPipelineMenuProps) => {
  const [startPipeline, { isLoading }] = useStartPipelineMutation();
  const { data: existingJobs = [] } = useGetProcessorJobsQuery({ projectId, packageId });

  const inferredSecondaryPackageId = useMemo(() => {
    const jobWithSecondary = [...existingJobs]
      .reverse()
      .find((job) => job.secondaryPackageId != null);
    return jobWithSecondary?.secondaryPackageId ?? null;
  }, [existingJobs]);

  const showTextProcessorSelect = canSelectTextProcessor(jobType, documentPackageType);

  const textProcessorOptions = useMemo(() => {
    const processors = documentPackageType
      ? (PROCESSORS_BY_PACKAGE_TYPE[documentPackageType] ?? [])
      : [];
    return [
      { label: 'All', value: ALL_PROCESSORS_VALUE },
      ...processors.map((name) => ({
        label: formatEnumToReadableText(name),
        value: name as string,
      })),
    ];
  }, [documentPackageType]);

  const pipelineOptions = useCallback(() => {
    const pipelineNames = getPipelinesForPackage(jobType, documentPackageType);
    return pipelineNames.map((name) => ({
      label: formatEnumToReadableText(name),
      value: name,
    }));
  }, [jobType, documentPackageType]);

  const [selectedPipeline, setSelectedPipeline] = useState<SelectOption>(pipelineOptions()[0]);
  const [selectedTextProcessor, setSelectedTextProcessor] = useState<SelectOption>(
    textProcessorOptions[0],
  );

  const isTextProcessingOnly =
    showTextProcessorSelect && selectedPipeline?.value === PipelineName.TEXT_PROCESSING_ONLY;

  const isSingleProcessor =
    isTextProcessingOnly && selectedTextProcessor?.value !== ALL_PROCESSORS_VALUE;

  const handleTrigger = async () => {
    if (!selectedPipeline) return;
    try {
      await startPipeline({
        projectId,
        packageId,
        pipelineName: selectedPipeline.value as PipelineName,
        jobType,
        ...(inferredSecondaryPackageId != null && {
          secondaryPackageId: inferredSecondaryPackageId,
        }),
        ...(isSingleProcessor && {
          metadata: { processor_name: selectedTextProcessor.value },
        }),
      }).unwrap();
      successToast({
        message: isSingleProcessor
          ? 'Text processor started successfully'
          : 'Pipeline started successfully',
      });
    } catch (error) {
      console.error('Failed to trigger:', error);
      errorToast({
        message: isSingleProcessor ? 'Failed to start text processor' : 'Failed to start pipeline',
      });
    }
  };

  return (
    <div className="flex gap-2">
      <Select
        options={pipelineOptions()}
        placeholder="Select Pipeline"
        value={selectedPipeline}
        onValueChange={(value) => setSelectedPipeline(value)}
        getOptionLabel={(option) => option?.label ?? ''}
        getOptionValue={(option) => (option ? String(option.value) : '')}
      />
      {isTextProcessingOnly && (
        <Select
          options={textProcessorOptions}
          placeholder="Select Processor"
          value={selectedTextProcessor}
          onValueChange={(value) => setSelectedTextProcessor(value)}
          getOptionLabel={(option) => option?.label ?? ''}
          getOptionValue={(option) => (option ? String(option.value) : '')}
        />
      )}
      <Button onClick={handleTrigger} disabled={isLoading}>
        {isLoading ? 'Starting...' : isSingleProcessor ? 'Trigger Processor' : 'Trigger Pipeline'}
      </Button>
    </div>
  );
};
